import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  generateClassNameByBehaviorSettings,
  behaviorSettingsProps,
} from '../../../../utils/behaviorSettings';
import { Form } from '../../../01_atoms/FormElements';
import MoneyHandles from './Parts/MoneyHandles/MoneyHandles';
import Details from './Parts/Details';
import { DEFAULT_COUNTRY_CODE, RG, SD } from '../../../../utils/constants';
import GiftAidPopup from './Parts/GiftAidPopup';
import Payments from './Parts/Payments';
import { submitDonation } from '../../../../api/donation';
import { PAYMENT_MODE, PAYMENT_MODE_TEST } from '../../../../utils/payments';
import ReCaptchaGoogle from '../../../01_atoms/ReCaptchaGoogle';
import LoadingText from '../../../01_atoms/LoadingText';
import Popup from '../../../02_molecules/Popup';
import {
  cancelPaymentOnDrupal,
  completeOrder,
  hasReturnUrlValuesInQuery,
} from '../../../02_molecules/RevolutStripeForm';
import {
  getFundraisingCosts,
  isFundraisingCostsApplied,
  isFundraisingCostsVisible,
  normalisePreferencesValues,
} from '../../../../utils/donationForm';
import { showCaptchaChallenge as showCaptchaChallengeCallback } from '../../../../utils/captcha';
import {
  pushViewItem as gtmPushViewItem,
  pushPurchase as gtmPushPurchase,
  pushDonationFormSubmissionError,
} from '../../../../utils/gtm';
import fetch from '../../../../utils/fetch';
import { getAttribution, isValidGUID } from '../../../../utils/attribution';

import styles from './index.module.scss';

const debug = require('debug')('cw:inlineDonationForm');
const rollbar = require('../../../../utils/rollbar');

const BBInlineDonationForm = ({
  uuid = null,
  optionsSingle,
  optionsMonthly,
  additionalMoneyHandles,
  impactsSingleDonation,
  impactsMonthlyDonation,
  initialValues,
  behaviorSettings = null,
  withoutAddress = false,
  withoutPhone = false,
  standaloneCheckoutPage = true,
  preferencesToView,
  campaigns,
  campaignsRG,
  minAmount,
  style = 'default',
  monthlyInfoText = '',
  showFundraisingCosts = false,
  fundraisingCostsInitValue = '',
  fundraisingCostsTooltip,
  showRevolutStripe = false,
  showCompanyDonation = false,
}) => {
  const [orderId, setOrderId] = useState(null);
  const [isProcessing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isCaptchaExecuted, setCaptchaExecuted] = useState(false);
  const reCaptchaRef = useRef();
  const router = useRouter();
  const forceCaptcha = parseInt(router.query.forceCaptcha, 10) === 1;
  const defaultAmount = parseInt(router.query.amount, 10);
  const [isGiftAidVisible, setGiftAidVisible] = useState(false);
  const [continuePaymentProcess, setContinuePaymentProcess] = useState(null);
  const isGiftAidSubmitted = useRef();
  const isReturnPage = hasReturnUrlValuesInQuery(router.query);

  // Pushing view item event to GTM.
  useEffect(() => {
    if (initialValues.appeal_id && standaloneCheckoutPage && !isReturnPage) {
      gtmPushViewItem([
        {
          item_id: initialValues.appeal_id,
          item_name: initialValues.appeal_title,
          item_category: 'Appeal',
          item_variant: initialValues.donation_type,
        },
      ]);
    }
  }, [
    uuid,
    standaloneCheckoutPage,
    initialValues.appeal_id,
    initialValues.appeal_title,
    initialValues.donation_type,
  ]);

  // The block supports multiple sets of money handles, the default ones
  // and additional ones, which are displayed if URL contains appropriate
  // source code value.
  const sourceCode = router.query.source;
  const moneyHandleSet =
    sourceCode &&
    typeof sourceCode === 'string' &&
    additionalMoneyHandles.find((item) => item.sourceCode === sourceCode);
  const moneyHandlesSingle = moneyHandleSet?.optionsSingle.length
    ? moneyHandleSet.optionsSingle
    : optionsSingle;
  const moneyHandlesMonthly = moneyHandleSet?.optionsMonthly.length
    ? moneyHandleSet.optionsMonthly
    : optionsMonthly;
  if (moneyHandleSet?.optionsSingle.length) {
    const preselectedSingleAmount = moneyHandleSet.preselectedSingle.length
      ? moneyHandleSet.preselectedSingle[0].value
      : 0;
    initialValues.money_handle.single = preselectedSingleAmount;
    initialValues.amount.single = preselectedSingleAmount || '';
    // If a new single money handle set is used,
    // updating initial fundraising costs as well.
    if (initialValues.previousAmount !== preselectedSingleAmount) {
      const fundraisingCostsAmount = getFundraisingCosts(preselectedSingleAmount);
      initialValues.fundraisingCostsAmount = fundraisingCostsAmount;
      initialValues.totalAmount = Number(preselectedSingleAmount) + Number(fundraisingCostsAmount);
      initialValues.previousAmount = preselectedSingleAmount;
    }
  }
  if (moneyHandleSet?.optionsMonthly.length) {
    const preselectedMonthlyAmount = moneyHandleSet.preselectedMonthly.length
      ? moneyHandleSet.preselectedMonthly[0].value
      : 0;
    initialValues.money_handle.monthly = preselectedMonthlyAmount;
    initialValues.amount.monthly = preselectedMonthlyAmount || '';
  }

  // Handle 'type': 'single'|'monthly' param from query, and apply default donation type.
  // It is needed to show the right form state in case of error in payment with redirect (Revolut).
  const defaultType = router.query.type;
  if (
    defaultType &&
    defaultType !== initialValues.donation_type &&
    ((defaultType === 'single' && moneyHandlesSingle.length > 0) ||
      (defaultType === 'monthly' && moneyHandlesMonthly.length > 0))
  ) {
    initialValues.donation_type = defaultType;
  }

  // If amount was passed as a query - use the amount by default.
  if (defaultAmount && !isNaN(defaultAmount) && defaultAmount > 0) {
    initialValues.amount[initialValues.donation_type] = `${defaultAmount}`;
    initialValues.money_handle[initialValues.donation_type] = `${defaultAmount}`;
    // Updating fundraising costs.
    if (
      initialValues.donation_type === 'single' &&
      initialValues.previousAmount !== defaultAmount
    ) {
      const fundraisingCostsAmount = getFundraisingCosts(defaultAmount);
      initialValues.fundraisingCostsAmount = fundraisingCostsAmount;
      initialValues.totalAmount = Number(defaultAmount) + Number(fundraisingCostsAmount);
      initialValues.previousAmount = defaultAmount;
    }
  }

  const attribution = getAttribution();
  // Pre-populate the source code field if the source code is passed in URL.
  if (router.query.source && isValidGUID(router.query.source)) {
    initialValues.source_code = router.query.source;
  }
  // Pre-populate the source code field if the source code for this appeal
  // is available in session data.
  else if (attribution && attribution.appeal_id === initialValues.appeal_id) {
    initialValues.source_code = attribution.source_code;
  }

  // Handles return to the form after payment with redirect (Revolut).
  useEffect(() => {
    if (isReturnPage) {
      if (!isProcessing) {
        setProcessing(true);
      }
      if (router.query.redirect_status !== 'succeeded') {
        // Cancel order and show an error.
        cancelPaymentOnDrupal(router.query.order_id, router.query.payment_id);
        onError({
          message:
            "Sorry, we couldn't complete your donation. Please review your details and try again or select an alternative payment method.",
          subline: '',
          additionalMessage: '',
          paymentMethodId:
            router.query.type === 'single'
              ? `decoupled_stripe_revolut_${PAYMENT_MODE}`
              : `decoupled_stripe_revolut_rg_${PAYMENT_MODE}`,
        });
        setProcessing(false);
      } else {
        const intentId =
          router.query.type === 'single' ? router.query.payment_intent : router.query.setup_intent;
        handleSuccessReturn(router.query.order_id, router.query.payment_id, intentId);
      }
    }
  }, []);

  // Making sure Captcha is activated on backend.
  // Checking if either we are forcing captcha for testing purposes
  // or we are in production.
  const showCaptchaChallenge = showCaptchaChallengeCallback.bind(null, {
    showGoogleAppleCaptcha: initialValues.show_google_apple_captcha,
    showCreditCardCaptcha: initialValues.show_credit_card_captcha,
  });

  const redirectToThankYouPage = useCallback((id, values) => {
    // Store donation details to display them on the 'Thank you' page.
    // Few body blocks use the data.
    window.sessionStorage.setItem(
      'donationDetails',
      JSON.stringify({
        name: values.first_name,
        orderId: id,
        totalAmount: isFundraisingCostsApplied(values)
          ? values.totalAmount
          : values.amount[values.donation_type],
      }),
    );

    router.push(`${values.thank_you_page}?type=${values['donation_type']}&orderID=${id}`);
  }, []);

  // Pushing purchase event to GTM.
  const pushSubmittedDataToGtm = useCallback((id, values) => {
    const formContactPreferences = values.contact_preferences || [];
    const contactPreferences = normalisePreferencesValues(
      formContactPreferences,
      preferencesToView,
    );

    const amount = isFundraisingCostsApplied(values)
      ? values.totalAmount
      : values.amount[values.donation_type];
    const transactionData = {
      id: id,
      revenue: amount,
      option: 'Inline',
    };

    const isFundraisingCostsAsked =
      showFundraisingCosts && isFundraisingCostsVisible(values, 'inline');

    const isCompanyDonation =
      values.company_donation && values.company_donation[0] === 'company_donation';

    const products = [
      {
        name: initialValues.appeal_title,
        id: initialValues.appeal_id,
        price: amount,
        category: values.donation_type === 'monthly' ? 'Monthly donation' : 'Single donation',
        variant: values.donation_type,
        quantity: 1,
        contact_preferences: contactPreferences,
        source_code: values.source_code,
        fundraising_costs_applied: isFundraisingCostsAsked
          ? isFundraisingCostsApplied(values)
          : 'not_asked',
        fundraising_costs_amount: values.fundraisingCostsAmount,
        company_donation: showCompanyDonation ? isCompanyDonation : 'not_asked',
      },
    ];
    gtmPushPurchase(transactionData, products);
  }, []);

  const proceedWithGiftAidPopup = (continueWithPayment) => {
    // Store function to proceed with GooglePay/ApplePay donation.
    // It comes from StripePaymentButtonElement.
    setContinuePaymentProcess(() => continueWithPayment);
    setGiftAidVisible(true);
  };

  const onError = useCallback((error) => {
    setError(error);
    setContinuePaymentProcess(null);
    setGiftAidVisible(false);
    isGiftAidSubmitted.current = false;
    setProcessing(false);
    // Don't reuse same order in case of error. We always create
    // new one during the next payment.
    setOrderId(null);

    // Push "pushDonationFormSubmissionError" GTM event if paymentMethodId is defined.
    if (error.paymentMethodId) {
      pushDonationFormSubmissionError(`payment.${error.paymentMethodId}`, error.message);
    }
  }, []);

  // Handles successful return case.
  const handleSuccessReturn = async (orderID, paymentId, intentId) => {
    try {
      // This function always return success.
      await completeOrder(orderID, paymentId);

      // TODO: Add GiftAid block on the Thank you page if it wasn't ticked.
      //   The flag value is available in router.query.giftaid.
      //   An example of the implementation: DonationFormPage/index.js
      //
      // Making a secure request to the backend to obtain order details.
      const responseMetadata = await fetch(`/commerce/order/${orderID}/metadata`, {
        query: { payment_id: paymentId, intent_id: intentId },
      });

      // Redirect to thank you page.
      let submissionValues = {
        thank_you_page: initialValues.thank_you_page,
        donation_type: router.query.type,
      };

      if (responseMetadata.body?.data) {
        // Contact preferences from backend don't take into account default
        // preference value, and it leads to wrong results in normalisePreferencesValues()
        // called in pushSubmittedDataToGtm().
        const contactPreferences = responseMetadata.body.data.contact_preferences;
        let alignedContactPreferences = [];
        preferencesToView.forEach(({ value, valueBackend }) => {
          if (contactPreferences.includes(value) && valueBackend !== 'denied') {
            alignedContactPreferences.push(value);
          }
        });
        responseMetadata.body.data.contact_preferences = alignedContactPreferences;
        // Recalculating total amount and fundraising cost since they are not available
        // on the backend if not applied, but reflected in analytics still.
        if (!responseMetadata.body.data.fundraisingCostsApplied) {
          const fundraisingCostsAmount = getFundraisingCosts(
            responseMetadata.body.data.totalAmount,
          );
          responseMetadata.body.data.totalAmount =
            Number(responseMetadata.body.data.totalAmount) + Number(fundraisingCostsAmount);
          responseMetadata.body.data.fundraisingCostsAmount = fundraisingCostsAmount;
        }

        submissionValues = {
          ...responseMetadata.body.data,
          ...submissionValues,
        };

        // Values returned from backend is not full-fledged replacement of form values,
        // it contains only data required by following function.
        pushSubmittedDataToGtm(orderID, responseMetadata.body.data);
      }

      redirectToThankYouPage(orderID, submissionValues);
    } catch (error) {
      if (rollbar) {
        rollbar.error(`Error during completing order with id ${orderID}.`, { error });
      }
    }
    setProcessing(false);
  };

  // Currently PayPal payment button is waiting Promise,
  // therefore we have it here.
  const onFormSubmit = useCallback(
    async (values) => {
      // Gift Aid popup for the UK.
      if (
        DEFAULT_COUNTRY_CODE === 'GB' &&
        !isGiftAidSubmitted.current &&
        /^(direct_debit|decoupled_stripe_credit_card|decoupled_stripe_revolut|decoupled_stripe_button_apple_pay|decoupled_stripe_button_google_pay)_/.test(
          values.payment_method_id,
        )
      ) {
        // Show GiftAid popup.
        setGiftAidVisible(true);
        return;
      }

      if (!isProcessing) {
        setProcessing(true);
      }

      if (
        // Checking if it's only a credit/debit card checkout
        showCaptchaChallenge(values.payment_method_id, 'credit_card') &&
        (forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST) &&
        // Make sure we haven't validated any challenge yet.
        !isCaptchaExecuted
      ) {
        // Set the flag that captcha has started execution to disable form submission.
        setCaptchaExecuted(true);
        // Trigger actual captcha execution.
        return reCaptchaRef.current.execute();
      }
      return await onOrderCreate(values);
    },
    [isGiftAidSubmitted, setGiftAidVisible, showCaptchaChallenge, isCaptchaExecuted],
  );

  const onOrderCreate = useCallback(
    (values) =>
      new Promise((resolve, reject) => {
        // Reset submission error to scroll user in case of another submission error.
        setError('');
        if (!isProcessing) {
          setProcessing(true);
        }

        let paymentOptions = values.payment_options || {};
        // Prepare sort code for UK Direct Debit.
        if (
          DEFAULT_COUNTRY_CODE === 'GB' &&
          values.payment_method_id.startsWith('direct_debit') &&
          paymentOptions.sort_code !== undefined
        ) {
          paymentOptions = {
            ...paymentOptions,
            sort_code: paymentOptions.sort_code.join(),
          };
        }

        const companyName =
          values.company_donation && values.company_donation[0] === 'company_donation'
            ? values.company_name
            : '';

        submitDonation(
          initialValues.appeal_id,
          values.donation_type === 'monthly' ? RG : SD,
          isFundraisingCostsApplied(values)
            ? values.totalAmount
            : values.amount[values.donation_type],
          // Do not send paypal/stripe payment data alongside with donation request,
          // because it should be handled separately.
          /^(paypal|decoupled_stripe)_/.test(values.payment_method_id)
            ? ''
            : values.payment_method_id,
          paymentOptions,
          values.source_code || '',
          values.first_name,
          values.last_name,
          values.email,
          withoutPhone ? '' : values.phone,
          values.contact_preferences,
          withoutAddress ? {} : values.address,
          !!values.gift_aid,
          '',
          preferencesToView,
          '',
          // We don't need Captcha token during Create order process, because we
          // need it only for Payment create process. Since we create CC/Apple/Google
          // payments separately we, the token will be provided from the 'values.captcha_token'.
          '',
          [],
          isFundraisingCostsApplied(values) ? values.fundraisingCostsAmount : 0,
          companyName,
        )
          .then((result) => {
            if (result.orderID && result.orderStatus !== 'completed') {
              setOrderId(result.orderID);
              resolve(result.orderID);
            } else if (result.orderID && result.orderStatus === 'completed') {
              // TODO: Add GiftAid block on the Thank you page if it wasn't ticked.
              //   The flag value is available in values.gift_aid.
              //   An example of the implementation: DonationFormPage/index.js
              pushSubmittedDataToGtm(result.orderID, values);
              redirectToThankYouPage(result.orderID, values);
            } else if (result.errorMessage) {
              onError({
                message: result.errorMessage,
                subline: '',
                additionalMessage: '',
                paymentMethodId: values.payment_method_id,
              });
              reject();
            } else {
              onError({
                message:
                  "Sorry, we couldn't submit the form. Please review your payment details and try again.",
                subline: '',
                additionalMessage: '',
                paymentMethodId: values.payment_method_id,
              });
              reject();
            }
          })
          .catch((e) => {
            onError({
              message:
                "Sorry, we couldn't submit the form. Please review your payment details and try again.",
              subline: '',
              additionalMessage: '',
              paymentMethodId: values.payment_method_id,
            });
            reject(e);
          });
      }),
    [],
  );

  const onSuccessPayment = (id, values) => {
    // TODO: Add GiftAid block on the Thank you page if it wasn't ticked.
    //   The flag value is available in values.gift_aid.
    //   An example of the implementation: DonationFormPage/index.js
    pushSubmittedDataToGtm(id, values);
    redirectToThankYouPage(id, values);
  };

  return (
    <div
      className={`bb bb-inline-donation-form ${
        styles['bb-inline-donation-form']
      } ${generateClassNameByBehaviorSettings(behaviorSettings)}`}
      id={uuid}
    >
      <Form
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        mutators={{
          // Create a mutator which allows to modify field's value by external callbacks.
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        validate={(values) => {
          const errors = {};
          // Extra validation for IE address when address fields are hidden.
          if (values.address.country.value === 'IE' && !withoutAddress) {
            if (!values.address.locality || !values.address.addressLine1) {
              errors['address'] = {};
              if (!values.address.locality) {
                errors['address']['locality'] = 'Please enter your address';
              }
              if (!values.address.addressLine1) {
                errors['address']['addressLine1'] = 'Please add town/city for this address';
              }
            }
          }
          return errors;
        }}
        subscription={{ valid: true, values: true }}
        render={({ handleSubmit, values, form: { mutators } }) => (
          <form onSubmit={handleSubmit}>
            <MoneyHandles
              optionsSingle={moneyHandlesSingle}
              optionsMonthly={moneyHandlesMonthly}
              impactsSingleDonation={impactsSingleDonation}
              impactsMonthlyDonation={impactsMonthlyDonation}
              minAmount={minAmount}
              style={style}
              monthlyInfoText={monthlyInfoText}
            />
            <Details
              withoutAddress={withoutAddress}
              withoutPhone={withoutPhone}
              preferencesToView={preferencesToView}
              campaigns={campaigns}
              campaignsRG={campaignsRG}
              showCompanyDonation={showCompanyDonation}
              campaign={initialValues.source_code ?? ''}
            />

            {isGiftAidVisible && (
              <GiftAidPopup
                continuePayment={continuePaymentProcess}
                setGiftAidVisible={setGiftAidVisible}
                isGiftAidSubmitted={isGiftAidSubmitted}
                style={style}
              />
            )}

            <Payments
              error={error}
              onError={onError}
              setProcessing={setProcessing}
              orderId={orderId}
              reCaptcha={reCaptchaRef ? reCaptchaRef.current : null}
              onSuccessPayment={(id) => onSuccessPayment(id, values)}
              submitFormWithValidationErrors={onFormSubmit}
              proceedWithGiftAidPopup={proceedWithGiftAidPopup}
              style={style}
              showFundraisingCosts={showFundraisingCosts}
              fundraisingCostsTooltip={fundraisingCostsTooltip}
              fundraisingCostsInitValue={fundraisingCostsInitValue}
              showRevolutStripe={showRevolutStripe}
            />

            {showCaptchaChallenge(values.payment_method_id) &&
              (forceCaptcha || PAYMENT_MODE !== PAYMENT_MODE_TEST) && (
                <ReCaptchaGoogle
                  forwardedRef={reCaptchaRef}
                  onCaptchaError={() => {
                    // Silently reload the captcha and set the captcha challenge to false,
                    // so that the next form submission will trigger the new captcha challenge.
                    reCaptchaRef.current.reset();
                    setCaptchaExecuted(false);
                  }}
                  onCaptchaChallenge={async (token) => {
                    try {
                      // Set token's value as a form field value.
                      // The value will be used by Stripe CC/Apple/Google payments.
                      mutators.setValue('captcha_token', token);

                      // Submit the whole donation form without validating it.
                      // Validation process was already completed before Captcha challenge.
                      // We don't have captcha token at this stage, and we don't need it during
                      // the Create Order process, because payment for the order will be created
                      // separately.
                      // We don't wait the process
                      await onOrderCreate(values);
                      // As soon as the form is submitted we want to reset the captcha
                      // challenge, so that the next form submit (i.e. in case of error)
                      // will execute a new challenge with the new token. Otherwise
                      // Google will complain on duplicated tokens sent multiple times.
                      reCaptchaRef.current.reset();
                      setCaptchaExecuted(false);
                    } catch (e) {
                      if (rollbar) {
                        rollbar.error('Could not handle submission with captcha', e);
                      }
                      debug('Could not handle submission with captcha: %O', e);
                    }
                  }}
                />
              )}
          </form>
        )}
      />

      {isProcessing && (
        <Popup
          isVisible
          className={styles['bb-inline-donation-form__loading']}
          onPopupClose={() => {}}
        >
          <LoadingText text="Please wait" />
        </Popup>
      )}
    </div>
  );
};

BBInlineDonationForm.propTypes = {
  uuid: PropTypes.string,
  behaviorSettings: behaviorSettingsProps,
  standaloneCheckoutPage: PropTypes.bool,
  withoutAddress: PropTypes.bool,
  withoutPhone: PropTypes.bool,
  preferencesToView: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      valueBackend: PropTypes.string,
      label: PropTypes.string,
      labelON: PropTypes.string,
    }),
  ).isRequired,
  optionsSingle: PropTypes.array.isRequired,
  optionsMonthly: PropTypes.array.isRequired,
  additionalMoneyHandles: PropTypes.array,
  impactsSingleDonation: PropTypes.array.isRequired,
  impactsMonthlyDonation: PropTypes.array.isRequired,
  initialValues: PropTypes.object.isRequired,
  minAmount: PropTypes.number.isRequired,
  campaigns: PropTypes.array.isRequired,
  campaignsRG: PropTypes.array.isRequired,
  style: PropTypes.oneOf(['default', 'emergency']),
  monthlyInfoText: PropTypes.string,
  showFundraisingCosts: PropTypes.bool,
  fundraisingCostsInitValue: PropTypes.bool,
  fundraisingCostsTooltip: PropTypes.string,
  showRevolutStripe: PropTypes.bool,
  showCompanyDonation: PropTypes.bool,
};

export default BBInlineDonationForm;
